import moment from "moment/moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ApplicationModal from "./ApplicationModal";
import EnrollCourse from "./EnrollCourse";
import axiosInstance from "../../../axios";
import { CheckCircle, Calendar, ArrowRight, CreditCard, Clock } from "lucide-react";

const formatCurrency = (amount, currency = "INR", locale = undefined) => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function roundBasedOnDigits(num) {
  const digits = Math.pow(10, num.toString().length - 1);
  return Math.round(num / digits) * digits;
}

const AdmissionFeeAndFinancing = ({ fetchCourse, loggedIn, courses, user }) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpen1, setModalOpen1] = useState(false);
  const { t } = useTranslation();
  const [currency, setCurrency] = useState("INR");

  useEffect(() => {
    async function fetchCurrency() {
      try {
        const data = await axiosInstance.get("/currency");
        if (data.currency) {
          setCurrency(data.currency);
        }
      } catch (error) {
        setCurrency("INR");
      }
    }
  }, []);

  const fee = Number(fetchCourse?.EnrollmentFee || 0);
  const originalFee = roundBasedOnDigits(fee + 5000);
  const savings = originalFee - fee;

  return (
    <section id="pricing" className="pricing-section">
      <div className="pricing-container">
        
        <div className="max-w-3xl text-left">
          <span className="pd-badge mb-4">
            Investment & Financing
          </span>
          <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight leading-none mt-2">
            Admission Fee & Financing
          </h1>
          <p className="mt-4 text-gray-500 text-[15px] leading-relaxed">
            The admission fee for this <strong>{fetchCourse?.CourseName}</strong> is {formatCurrency(fee)} (Incl. taxes). This fee also includes all applicable program charges, learning resources, and platform access.
          </p>
        </div>

        <div className="pricing-grid-card">
          {/* Left Column: Cost Summary */}
          <div className="pricing-card-left">
            <span className="price-label">Special Discount Price</span>
            <span className="price-amount">{formatCurrency(fee)}</span>
            <span className="price-original">{formatCurrency(originalFee)}</span>
            {savings > 0 && (
              <span className="savings-badge">Save {formatCurrency(savings)} Today!</span>
            )}
          </div>

          {/* Right Column: Financing & Installments */}
          <div className="pricing-card-right text-left">
            <div>
              <span className="installment-label">Pay In Installments</span>
              <span className="installment-amount block">
                {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Math.round(fee / 3))} / Month
              </span>
              <p className="installment-desc">
                We are dedicated to making our programs accessible. Budget for your program with a variety of financing options, low monthly installments, and economic payment terms.
              </p>

              <div className="financing-features-list">
                <div className="financing-feature-item">
                  <div className="w-6 h-6 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
                    <CheckCircle size={14} className="stroke-[2.5]" />
                  </div>
                  <span>No Cost EMI Options</span>
                </div>
                <div className="financing-feature-item">
                  <div className="w-6 h-6 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
                    <CreditCard size={14} className="stroke-[2.5]" />
                  </div>
                  <span>All Major Cards Accepted</span>
                </div>
                <div className="financing-feature-item">
                  <div className="w-6 h-6 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
                    <CheckCircle size={14} className="stroke-[2.5]" />
                  </div>
                  <span>100% Safe & Secure Checkout</span>
                </div>
                <div className="financing-feature-item">
                  <div className="w-6 h-6 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
                    <CheckCircle size={14} className="stroke-[2.5]" />
                  </div>
                  <span>Instantly Approved Plans</span>
                </div>
              </div>
            </div>

            <div className="financing-actions-row">
              <div className="flex flex-col gap-1">
                <span className="deadline-info flex items-center gap-1.5 font-semibold text-gray-400">
                  <Clock size={14} className="text-blue-500" />
                  Admission Closes: <strong className="text-gray-800">{moment(fetchCourse?.courseDate).format("MMM DD, YYYY")}</strong>
                </span>
                <a href="#" className="learn-more-link mt-1 font-bold">
                  Learn More Financing Options <ArrowRight size={14} />
                </a>
              </div>

              <div className="flex items-center gap-3">
                {!loggedIn ? (
                  <button
                    onClick={() => navigate("/register")}
                    className="btn-primary-custom !px-8 !py-3.5"
                  >
                    Apply Now
                  </button>
                ) : user.role === 2 && (
                  !courses?.isPurchased ? (
                    <button
                      onClick={() => setModalOpen1(true)}
                      className="btn-primary-custom !px-8 !py-3.5"
                    >
                      Buy Now
                    </button>
                  ) : (
                    <button className="inline-flex items-center gap-1.5 text-sm font-bold text-white px-6 py-3.5 rounded-xl cursor-default bg-gradient-to-r from-emerald-500 to-green-600 shadow-md shadow-emerald-100">
                      <CheckCircle size={16} />
                      Enrolled
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <ApplicationModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      <EnrollCourse isOpen={modalOpen1} onClose={() => setModalOpen1(false)} />
    </section>
  );
};

export default AdmissionFeeAndFinancing;

